import { useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import { PrimaryTableHeader, TableDataType } from 'app/components/elements/TableCustom';
import { downloadCsv } from 'utils/downloadCsv';
import {
  formatMaintenanceFrequency,
  formatSourceCounter,
  MaintenanceEngineDevice,
  MaintenanceStatus
} from 'services/maintenanceEngine';

export const STATUS_COLORS: Record<MaintenanceStatus, { bgcolor: string; color: string }> = {
  NORMAL: { bgcolor: '#DCFCE7', color: '#15803D' },
  UPCOMING: { bgcolor: '#FEF3C7', color: '#B45309' },
  DUE: { bgcolor: '#FEE2E2', color: '#DC2626' },
  OVERDUE: { bgcolor: '#FEE2E2', color: '#991B1B' }
};

export const MAINTENANCE_TABLE_HEADERS: PrimaryTableHeader[] = [
  { label: 'Asset Name', width: 220 },
  { label: 'Client Name', width: 180 },
  { label: 'Location', width: 180 },
  { label: 'IoT Device', width: 200 },
  { label: 'Current Reading', width: 160 },
  { label: 'Remaining Frequency', width: 160 },
  { label: 'Frequency Type', width: 160 },
  { label: 'Maintenance Frequency', width: 180 },
  { label: 'Maintenance Trigger Type', width: 200 },
  { label: 'Source Counter', width: 140 },
  { label: 'Status', width: 140 }
];

export const buildMaintenanceRow = (device: MaintenanceEngineDevice): TableDataType => ({
  assetName: { value: device.assetName },
  clientName: { value: device.clientName || '-' },
  location: { value: device.location || '-' },
  iotDevice: { value: device.iotDeviceName || device.iotDeviceId || '-' },
  currentReading: { value: device.currentReadingValue ?? '-' },
  remainingHours: { value: device.remainingHours ?? '-' },
  frequencyType: { value: device.frequencyType || '-' },
  maintenanceFrequency: { value: formatMaintenanceFrequency(device) },
  maintenanceTriggerType: { value: device.maintenanceTriggerType || '-' },
  sourceCounter: { value: formatSourceCounter(device) },
  status: {
    value: (
      <Chip
        size="small"
        label={device.maintenanceStatus}
        sx={{ ...STATUS_COLORS[device.maintenanceStatus], fontWeight: 700, fontSize: 11 }}
      />
    )
  }
});

const MAINTENANCE_CSV_HEADERS = [
  { key: 'assetName', label: 'Asset Name' },
  { key: 'clientName', label: 'Client Name' },
  { key: 'location', label: 'Location' },
  { key: 'iotDevice', label: 'IoT Device' },
  { key: 'currentReading', label: 'Current Reading' },
  { key: 'remainingHours', label: 'Remaining Frequency' },
  { key: 'frequencyType', label: 'Frequency Type' },
  { key: 'maintenanceFrequency', label: 'Maintenance Frequency' },
  { key: 'maintenanceTriggerType', label: 'Maintenance Trigger Type' },
  { key: 'sourceCounter', label: 'Source Counter' },
  { key: 'status', label: 'Status' }
];

export const exportMaintenanceCsv = (filename: string, devices: MaintenanceEngineDevice[]) => {
  const rows = devices.map((device) => ({
    assetName: device.assetName,
    clientName: device.clientName,
    location: device.location,
    iotDevice: device.iotDeviceName || device.iotDeviceId,
    currentReading: device.currentReadingValue,
    remainingHours: device.remainingHours,
    frequencyType: device.frequencyType,
    maintenanceFrequency: formatMaintenanceFrequency(device),
    maintenanceTriggerType: device.maintenanceTriggerType,
    sourceCounter: formatSourceCounter(device),
    status: device.maintenanceStatus
  }));
  downloadCsv(filename, MAINTENANCE_CSV_HEADERS, rows);
};

export const MAINTENANCE_STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Normal', value: 'NORMAL' },
  { label: 'Upcoming', value: 'UPCOMING' },
  { label: 'Due', value: 'DUE' },
  { label: 'Overdue', value: 'OVERDUE' }
];

export const useMaintenanceTableFilters = (
  devices: MaintenanceEngineDevice[],
  options?: { includeStatusFilter?: boolean; initialStatus?: MaintenanceStatus }
) => {
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [location, setLocation] = useState('');
  const [triggerType, setTriggerType] = useState('');
  const [status, setStatus] = useState(options?.initialStatus ?? '');
  const includeStatusFilter = options?.includeStatusFilter;

  const clientOptions = useMemo(() => {
    const unique = Array.from(
      new Set(devices.map((d) => d.clientName).filter(Boolean))
    ) as string[];
    return [
      { label: 'All Clients', value: '' },
      ...unique.sort().map((name) => ({ label: name, value: name }))
    ];
  }, [devices]);

  const locationOptions = useMemo(() => {
    const unique = Array.from(new Set(devices.map((d) => d.location).filter(Boolean))) as string[];
    return [
      { label: 'All Locations', value: '' },
      ...unique.sort().map((name) => ({ label: name, value: name }))
    ];
  }, [devices]);

  const triggerTypeOptions = useMemo(() => {
    const unique = Array.from(
      new Set(devices.map((d) => d.maintenanceTriggerType).filter(Boolean))
    ) as string[];
    return [
      { label: 'All Trigger Types', value: '' },
      ...unique.sort().map((name) => ({ label: name, value: name }))
    ];
  }, [devices]);

  const filteredDevices = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return devices.filter((device) => {
      if (client && device.clientName !== client) return false;
      if (location && device.location !== location) return false;
      if (triggerType && device.maintenanceTriggerType !== triggerType) return false;
      if (includeStatusFilter && status && device.maintenanceStatus !== status) return false;
      if (keyword) {
        const haystack = [
          device.assetName,
          device.clientName,
          device.location,
          device.iotDeviceName,
          device.iotDeviceId
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [devices, search, client, location, triggerType, status, includeStatusFilter]);

  const hasActiveFilters = Boolean(
    search || client || location || triggerType || (includeStatusFilter && status)
  );

  const clearFilters = () => {
    setSearch('');
    setClient('');
    setLocation('');
    setTriggerType('');
    setStatus('');
  };

  return {
    search,
    setSearch,
    client,
    setClient,
    clientOptions,
    location,
    setLocation,
    locationOptions,
    triggerType,
    setTriggerType,
    triggerTypeOptions,
    status,
    setStatus,
    filteredDevices,
    hasActiveFilters,
    clearFilters
  };
};
